<script setup lang="ts">
import AuthPasswordStrength from '@/components/AuthPasswordStrength/AuthPasswordStrength.vue';
import { useToast } from '@/use/toast';
import { useI18n } from '@/i18n';
import { isPasswordlessAccount } from '@/lib/providers';
import { refreshCurrentUser } from '@/lib/refreshUser';
import { createPasswordChangeSchema, type PasswordChangeFormValues } from '@/schemas/passwordChange';
import { useUserStore } from '@/stores/user';
import { Button, Form, Heading, LayoutGap, Prose } from '@dabble/quillui';
import { toTypedSchema } from '@vee-validate/yup';
import { useForm, useIsFormDirty } from 'vee-validate';
import { computed, onMounted, ref } from 'vue';

const { t, te } = useI18n();
const userStore = useUserStore();
const { showToast } = useToast();

function translateFieldError(raw: string | undefined) {
  if (!raw) return '';
  return te(raw) ? t(raw) : raw;
}

const isLoading = ref(false);

const rawPasswordChangeSchema = computed(() => createPasswordChangeSchema((key: string) => t(key)));
const validationSchema = computed(() => toTypedSchema(rawPasswordChangeSchema.value));

const { handleSubmit, defineField, values, resetForm } = useForm<PasswordChangeFormValues>({
  validationSchema,
  initialValues: {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  },
});

const isValid = computed(() =>
  rawPasswordChangeSchema.value.isValidSync({
    currentPassword: values.currentPassword ?? '',
    newPassword: values.newPassword ?? '',
    confirmPassword: values.confirmPassword ?? '',
  }),
);

const isDirty = useIsFormDirty();

const fieldOpts = {
  validateOnBlur: true,
  validateOnChange: true,
  validateOnModelUpdate: true,
  props: (state: { errors: string[] }) => ({
    error: state.errors.length > 0,
    errorMessage: translateFieldError(state.errors[0]),
  }),
};

const [currentPassword, currentPasswordField] = defineField('currentPassword', fieldOpts);
const [newPassword, newPasswordField] = defineField('newPassword', fieldOpts);
const [confirmPassword, confirmPasswordField] = defineField('confirmPassword', fieldOpts);

onMounted(async () => {
  if (userStore.user.uid) {
    // An expired session surfaces here as a 401 and redirects to login.
    await refreshCurrentUser(userStore.user.uid);
  }
});

/**
 * An account created through Google SSO (or whose password provider was
 * removed by Firebase's unverified-email auto-link) has no current password
 * to change, so we offer the emailed set-password flow instead.
 */
const isPasswordless = computed(() => isPasswordlessAccount(userStore.user));

const isSendingSetPassword = ref(false);

async function onSetPassword() {
  isSendingSetPassword.value = true;
  try {
    await userStore.requestPasswordReset(userStore.user.email);
    showToast('success', t('account_password_sso_sent'));
  } catch (error: any) {
    showToast('danger', error.message);
  } finally {
    isSendingSetPassword.value = false;
  }
}

const onSubmit = handleSubmit(async (formValues) => {
  isLoading.value = true;
  try {
    await userStore.updatePassword(userStore.user?.email || '', formValues.currentPassword, formValues.newPassword);
    resetForm({
      values: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      },
    });
    showToast('success', t('account_password_updated'));
  } catch (error: any) {
    showToast('danger', error.message);
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <LayoutGap direction="column" align="stretch" size="medium" fill class="password-view">
    <LayoutGap direction="column" align="stretch" size="xsmall" fill>
      <Heading :level="1" family="secondary" :margin="false">{{ t('account_password_title') }}</Heading>
      <Prose variant="secondary">{{ t('account_password_lede') }}</Prose>
    </LayoutGap>

    <LayoutGap
      v-if="isPasswordless"
      direction="column"
      align="stretch"
      size="small"
      fill
      class="password-view__content"
    >
      <Prose class="password-view__sso-description">{{ t('account_password_sso_description') }}</Prose>
      <Button class="password-view__set-password" :disabled="isSendingSetPassword" @click="onSetPassword">
        {{ isSendingSetPassword ? t('lost_password_sending') : t('account_password_sso_send') }}
      </Button>
    </LayoutGap>

    <LayoutGap v-else direction="column" align="stretch" size="small" fill class="password-view__content">
      <Form v-model:dirty="isDirty" :valid="isValid" :actions="false" class="password-view__form" @submit="onSubmit">
        <LayoutGap direction="column" align="stretch" size="small" fill>
          <AuthPasswordStrength
            id="password-current"
            v-model="currentPassword"
            v-bind="currentPasswordField"
            :label="t('account_password_current')"
            name="current-password"
            :show-requirements="false"
          />
          <AuthPasswordStrength
            id="password-new"
            v-model="newPassword"
            v-bind="newPasswordField"
            :label="t('account_password_new')"
            name="new-password"
            :show-requirements="true"
          />
          <AuthPasswordStrength
            id="password-confirm"
            v-model="confirmPassword"
            v-bind="confirmPasswordField"
            :label="t('account_password_confirm')"
            name="confirm-password"
            :show-requirements="false"
          />
          <Button fill-width class="password-view__submit" type="submit" :disabled="isLoading || !isValid || !isDirty">
            {{ isLoading ? t('account_password_updating') : t('account_password_submit') }}
          </Button>
        </LayoutGap>
      </Form>
    </LayoutGap>
  </LayoutGap>
</template>

<style lang="scss" scoped>
.password-view {
  max-width: 465px;

  &__content {
    background: theme('color-bg-app-panel');
  }

  &__form {
    width: 100%;
  }

  &__submit,
  &__set-password {
    margin-top: space('small');
    max-width: 211px;
  }
}
</style>
